import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button } from '~/ui/buttons/Button';
import { Select } from '~/ui/forms/Select';

const FormGroup = styled.div`
  margin-bottom: 24px;

  input,
  select,
  textarea {
    font-size: 12px;
    line-height: 1.5;
    font-family: Raleway;
    color: #2e2e2e;
    width: 100%;
    padding: 12px 15px;
    border: none;
    border-radius: 5px;
    background: #f5f5f5;
    box-sizing: border-box;

    &::placeholder {
      color: ##6f6f6f;
    }
  }
`;

const Label = styled.label<{ bold?: boolean }>`
  display: block;
  margin-bottom: 8px;
  font-family: ${props => (props.bold ? 'ralewayBold' : 'Raleway')};
  font-weight: ${props => (props.bold ? 400 : 500)};
  font-size: 12px;
  line-height: 1.5;
  color: #333;
`;

const RequiredAsterisk = styled.span`
  color: #9738ff;
  margin-left: 4px;
  &:before {
    content: '•';
  }
`;

const Input = styled.input``;

const TextArea = styled.textarea`
  min-height: 130px;
  resize: vertical;
`;

const HelperText = styled.p`
  font-family: Raleway;
  font-size: 12px;
  line-height: 1.5;
  color: #2e2e2e;
  margin: 8px 0 6px;
`;

const DisclaimerText = styled.p`
  font-family: Raleway;
  font-size: 11px;
  line-height: 1.5;
  color: #6f6f6f;
  margin: 8px 0 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0;
`;

const GenerateButton = styled(Button)`
  padding: 11px 24px;
  font-family: ralewayBold;
  font-size: 16px;
  line-height: 1.5;
  border-radius: 10px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

export interface ChatBotTemplate {
  id: string;
  name: string;
  description?: string;
}

export interface FormData {
  topic: string;
  questionCount: number;
  sensitiveTopics: string;
  selectedTemplate?: string;
  level: string;
}

export interface AIScenarioFormProps {
  formData: FormData;
  onFormDataChange: (formData: FormData) => void;
  onSubmit: (event: React.FormEvent) => void;
  isValid: boolean;
  templates?: ChatBotTemplate[];
  isSubmitting?: boolean;
  isEditing?: boolean;
}

export const AIScenarioForm: React.FC<AIScenarioFormProps> = ({
  formData,
  onFormDataChange,
  onSubmit,
  isValid,
  templates = [],
  isSubmitting = false,
  isEditing = false
}) => {
  const handleInputChange = (field: keyof FormData, value: string | number) => {
    onFormDataChange({
      ...formData,
      [field]: value
    });
  };

  useEffect(() => {
    if (templates.length === 1) {
      handleInputChange('selectedTemplate', templates[0].id);
    }
  }, [templates.length, isEditing]);

  return (
    <form onSubmit={onSubmit}>
      <FormGroup>
        <Label htmlFor="topic" bold={true}>
          What’s the main topic or scenario for this practice?
          <RequiredAsterisk />
        </Label>
        <Input
          id="topic"
          type="text"
          placeholder="Type here..."
          value={formData.topic}
          onChange={e => handleInputChange('topic', e.target.value)}
          required
        />
      </FormGroup>

      {templates.length > 1 && !isEditing && (
        <FormGroup>
          <Label htmlFor="template">Chatbot Template</Label>
          <Select
            id="template"
            value={formData.selectedTemplate || ''}
            onChange={e =>
              handleInputChange('selectedTemplate', e.target.value)
            }
            aria-label="Select chatbot template"
            aria-describedby="template-description"
            placeholder="Select a template..."
            options={templates.map(template => ({
              value: template.id,
              label: template.name
            }))}
          />
        </FormGroup>
      )}

      <FormGroup>
        <Label htmlFor="questionCount">
          How many questions should the AI ask the learner?
        </Label>
        <Select
          id="questionCount"
          value={formData.questionCount}
          onChange={e =>
            handleInputChange('questionCount', parseInt(e.target.value, 10))
          }
          aria-label="Select number of questions"
          options={Array.from({ length: 10 }, (_, i) => ({
            value: i + 1,
            label: (i + 1).toString()
          }))}
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="level">Level</Label>
        <Select
          id="level"
          value={formData.level}
          onChange={e => handleInputChange('level', e.target.value)}
          aria-label="Select difficulty level"
          options={[
            { value: 'Beginner', label: 'Beginner' },
            { value: 'Intermediate', label: 'Intermediate' },
            { value: 'Advanced', label: 'Advanced' }
          ]}
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="sensitiveTopics" bold={true}>
          Anything the AI should keep in mind?
        </Label>
        <HelperText>
          (e.g., learner’s experience level, sensitive topics to avoid, tone to
          use such as friendly/professional)
        </HelperText>
        <TextArea
          id="sensitiveTopics"
          placeholder="Type here..."
          value={formData.sensitiveTopics}
          onChange={e => handleInputChange('sensitiveTopics', e.target.value)}
        />
      </FormGroup>

      <ButtonContainer>
        <GenerateButton type="submit" disabled={!isValid || isSubmitting}>
          {isSubmitting
            ? isEditing
              ? 'Updating...'
              : 'Creating...'
            : isEditing
            ? 'Update Scenario'
            : 'Generate Scenario'}
        </GenerateButton>
      </ButtonContainer>
      <DisclaimerText>
        Legal note: When using this simulation template, you are solely
        responsible for the content you input. Do not enter any material that is
        inappropriate, infringes on third-party intellectual property rights, or
        violates an individual’s privacy.
      </DisclaimerText>
    </form>
  );
};
